import { useState } from 'react';
import { AppLayoutWithHeader } from '@/components/layout/AppLayoutWithHeader';
import { ContentContainer } from '@/components/layout/ContentContainer';
import { useMembresQuery, Membre } from '@/hooks/queries/useMembresQuery';
import { useOrganizationFeatures } from '@/hooks/useOrganizationFeatures';
import { EquipeKPICards } from '@/components/equipe/EquipeKPICards';
import { EquipeTable } from '@/components/equipe/EquipeTable';
import { MembreSheet } from '@/components/equipe/MembreSheet';
import { AddMembreMenu } from '@/components/equipe/AddMembreMenu';
import { AddMembreDialog } from '@/components/equipe/AddMembreDialog';
import { ImportCSVDialog } from '@/components/equipe/ImportCSVDialog';
import { ScanAttestationsDialog } from '@/components/equipe/ScanAttestationsDialog';

/**
 * Page Mon équipe — Sprint 2 + 4
 * Tableau des membres, 4 métriques, panel latéral, 3 modes d'ajout
 */
const MonEquipe = () => {
  const { data: membres, isLoading, isFetched, stats } = useMembresQuery();
  const { canManageMembers, loading: organizationLoading } = useOrganizationFeatures();
  
  const [selectedMembre, setSelectedMembre] = useState<Membre | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [scanDialogOpen, setScanDialogOpen] = useState(false);

  const handleSelectMembre = (membre: Membre) => {
    setSelectedMembre(membre);
    setSheetOpen(true);
  };

  return (
    <AppLayoutWithHeader
      title="Mon équipe 👥"
      subtitle="Gestion des membres et suivi des attestations d'honorabilité"
      layoutMode="default"
      sidebarVisible={true}
    >
      <ContentContainer className="w-full">
        <div className="flex w-full flex-col gap-4">
          {/* KPI Cards */}
          <EquipeKPICards stats={stats} />

          {/* Section tableau */}
          <section className="app-surface rounded-[22px] p-4">
            <div className="flex flex-col gap-3.5">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="max-w-[720px]">
                  <h2 className="text-[18px] font-semibold tracking-[-0.02em] text-[hsl(var(--app-text-strong))] md:text-[20px]">
                    Membres de l'équipe
                  </h2>
                </div>
                <div className="lg:pt-0.5">
                  <AddMembreMenu
                    onIndividuel={() => setAddDialogOpen(true)}
                    onCSV={() => setCsvDialogOpen(true)}
                    onScan={() => setScanDialogOpen(true)}
                    disabled={!canManageMembers}
                    loading={organizationLoading}
                  />
                </div>
              </div>

              {/* Tableau */}
              <EquipeTable
                membres={membres || []}
                loading={isLoading}
                fetched={isFetched}
                onSelectMembre={handleSelectMembre}
              />
            </div>
          </section>
        </div>
      </ContentContainer>

      {/* Panel latéral fiche membre */}
      <MembreSheet
        membre={selectedMembre}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
      />

      {/* Modals d'ajout */}
      <AddMembreDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} />
      <ImportCSVDialog open={csvDialogOpen} onOpenChange={setCsvDialogOpen} />
      <ScanAttestationsDialog open={scanDialogOpen} onOpenChange={setScanDialogOpen} />
    </AppLayoutWithHeader>
  );
};

export default MonEquipe;
